import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

// PNU 내부 공동연구자 리스트 (데이터 주입형)
// - 외부에서 전체 데이터를 내려주면, 여기서만 로컬 페이징 처리
export default function PnuCoResearchers({ active, data = [], perPage = 6, loading = false, linkMode = 'path' }) {
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const rows = Array.isArray(data) ? data : [];
  const totalResults = rows.length;
  const totalPage = Math.max(1, Math.ceil(totalResults / perPage));
  const pageSafe = Math.min(Math.max(1, page), totalPage);
  const start = (pageSafe - 1) * perPage;
  const slice = rows.slice(start, start + perPage);

  const skeletonItems = useMemo(() => Array.from({ length: perPage }), [perPage]);

  const renderCount = (item) => {
    const n = Number(item?.count ?? 0) || 0;
    try { return n.toLocaleString(); } catch { return String(n); }
  };

  const nameText = (a) => {
    const eng = a?.author_name_eng || '';
    const name = a?.author_name || '';

    if (eng && name && eng !== name) return `${eng}(${name})`;
    return name || '—';
  };

  const orgText = (item) => item?.dept_name || item?.org_name || '';

  const hasRows = Array.isArray(slice) && slice.length > 0;

  return (
    <div>
      <div className="relative min-h-[220px]">
        {(loading || !active) && (
          <ChartSkeletonLoader show={true} variant="cards" overlay={true} />
        )}
        <ul className="flex flex-col gap-2 md:flex-row md:flex-wrap lg:gap-3">
        {active && !loading && hasRows && slice.map((item, idx) => {
          const id = item?.scholar_id;
          const canNavigate = id != null && String(id).trim() !== '';
          const to = linkMode === 'query'
            ? `/researchers/?id=${encodeURIComponent(id)}`
            : `/researchers/${encodeURIComponent(id)}`;
          const onCardClick = (e) => {
            try { if (e) e.preventDefault(); } catch {}
            if (!canNavigate) return;
            navigate(to);
            try {
              if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }), 0);
              }
            } catch {}
          }
          const onKey = (e) => {
            if (e.key === 'Enter' || e.key === ' ') onCardClick(e);
          };
          return (
          <li
            key={item?.scholar_id ? `${item.scholar_id}-${idx}` : idx}
            className={`relative w-[calc(50%-8px)] p-[18px] border border-[#E4E8F1] rounded-xl transition duration-300 xl:w-[calc((100%-60px)/3)] hover:shadow-[0px_4px_20px_rgba(0,0,0,.05)] 2xl:p-[30px] ${canNavigate ? 'cursor-pointer' : ''}`}
            role={canNavigate ? 'button' : undefined}
            tabIndex={canNavigate ? 0 : -1}
            aria-label={canNavigate ? `${nameText(item)} 프로필로 이동` : undefined}
            onClick={onCardClick}
            onKeyDown={onKey}
          >
            <a href="#" onClick={(e) => e.preventDefault()}>
              <div className="w-[55px] h-[55px] mx-auto mb-[10px] overflow-hidden rounded-full border border-[#E4E8F1] lg:mx-0 lg:w-[72px] lg:h-[72px] lg:mb-[14px]">
                <img src={`${rmVars.themeUrl}images/profile.png`} alt="photo" />
              </div>

              <div className="name px-4 text-[13px] leading-[140%] font-bold text-center lg:text-left lg:text-[16px] lg:px-0">
                {nameText(item)}
              </div>

              <div className="block px-4 text-[11px] text-[#8D9AAF] leading-[130%] text-center font-normal mt-[6px] lg:text-[12px] lg:text-left lg:px-0">
                {orgText(item)}
              </div>

              {/* 배지: 데이터 연동 전까지 숨김 처리 (필요 시 조건부 렌더링) */}
              <div className="flex flex-col items-center gap-1 mt-[10px] lg:flex-row lg:flex-wrap hidden">
                <div className="inline-flex items-center gap-x-1 px-[10px] py-[2px] text-[11px] leading-[140%] font-semibold text-[#F3652D] bg-[#FFE9E9] rounded-full">
                  <img src={`${rmVars.themeUrl}images/world-top-ico-orange.png`} className="w-3" alt="world top" />
                  <span>World Top 2%</span>
                </div>

                <div className="inline-flex items-center gap-x-1 px-[10px] py-[2px] text-[11px] leading-[140%] font-semibold text-[#8D6DE2] bg-[#EDE7FF] rounded-full">
                  <img src={`${rmVars.themeUrl}images/hcr-ico-purple.png`} className="w-3" alt="hcr" />
                  <span>HCR</span>
                </div>
              </div>

              <div className="flex justify-between items-center w-full px-4 pt-4 mt-4 font-bold leading-[130%] border-t border-t-[#E4E8F1] lg:px-[16px] lg:py-[10px] lg:border lg:border-[#E4E8F1] lg:rounded-xl">
                <p className="text-[13px] text-[#6C7687] lg:text-[14px]">
                  Publication <br />Count
                </p>
                <p className="text-[16px] lg:text-[14px]">{renderCount(item)}</p>
              </div>

              {/* 관심 아이콘: 디자인만 반영, 현재는 숨김 */}
              {/*<div className="intrest hidden absolute lg:block top-[30px] right-[30px] w-[38px] h-[38px] cursor-pointer group">*/}
              {/*  <img src={`${rmVars.themeUrl}images/intrest-default.png`} className="interest-default group-hover:hidden" alt="interest-default" />*/}
              {/*  <img src={`${rmVars.themeUrl}images/intrest-hover.png`} className="interest-hover hidden group-hover:block" alt="interest-hover" />*/}
              {/*  <img src={`${rmVars.themeUrl}images/intrest-active.png`} className="interest-active hidden group-hover:hidden" alt="interest-active" />*/}
              {/*</div>*/}
            </a>
          </li>
          );
        })}

        {active && !loading && !hasRows && (
          <li className="w-full px-6 py-[22px] text-center text-[13px] text-[#6C7687] border border-[#E4E8F1] rounded-[12px]">데이터가 없습니다.</li>
        )}
      </ul>
      </div>

      {!loading && totalPage > 1 && (
        <div className="flex items-center justify-center gap-2 font-[Pretendard] text-[15px] mt-[50px] lg:mt-[40px]">
          <button className="shrink-0 p-2 rounded-md cursor-pointer" aria-label="prev" onClick={() => setPage(p => Math.max(1, p - 1))}>
            <img src={`${rmVars.themeUrl}images/pager_prev.png`} alt="prev" className="w-4 h-4" />
          </button>
          {Array.from({ length: totalPage }).slice(0, 5).map((_, i) => {
            const p = i + 1;
            const isActive = p === pageSafe;
            return (
              <button key={p} className={`px-[15px] py-2 rounded-md font-medium cursor-pointer ${isActive ? 'text-[#215BD8] bg-[#D7E2F9]' : 'text-[#6C7687] hover:bg-[#E4E8F1]'}`} onClick={() => setPage(p)}>
                {p}
              </button>
            );
          })}
          {totalPage > 5 && <button className="text-[#6C7687] select-none px-1">...</button>}
          {totalPage > 5 && (
            <button className={`px-[15px] py-2 rounded-md text-[#6C7687] text-[15px] hover:bg-[#E4E8F1] font-medium cursor-pointer`} onClick={() => setPage(totalPage)}>
              {totalPage}
            </button>
          )}
          <button className="shrink-0 p-2 rounded-md cursor-pointer" aria-label="next" onClick={() => setPage(p => Math.min(totalPage, p + 1))}>
            <img src={`${rmVars.themeUrl}images/pager_next.png`} alt="next" className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

